import React, { useState, useEffect, useRef, useCallback } from "react";
import axiosApi from "../server/service";
import {
  CreateSizeSegmentForm,
  NewSegmentData,
} from "../component/CreateSizeSegmenForm";
import toast from "react-hot-toast";
import { Link } from "react-router-dom";
import {

  Package,
  ArrowLeft,
  Plus,
  Search,

  Loader2,

} from "lucide-react";

import {
    Input, Button, Badge,
    ShadcnTable, ShadcnTableHeader, ShadcnTableBody, ShadcnTableRow, ShadcnTableHead, ShadcnTableCell,
     ActionMenu 
} from "../component/ui/TableComponent";


interface IApiSizeSegment {
  _id: string;
  segmentName: string;
  status: "Active" | "Inactive";
  // length: number;
  // width: number;
  numberOfSets: number;
  createdAt: string;
  updatedAt: string;
}

interface TransformedSegmentData {
  id: string;
  name: string;
  status: "Active" | "Inactive";
  // length: number;
  // width: number;
  sets: number;
  lastUpdated: string;
}

// --- HELPER FUNCTION & ACTION MENU ---
const formatDateTime = (iso?: string): string =>
  iso ? new Date(iso).toLocaleString() : "—";

// interface ActionMenuProps {
//   onEdit: () => void;
//   onDelete: () => void;
// }
// const ActionMenu: React.FC<ActionMenuProps> = ({ onEdit, onDelete }) => {
//   const [isOpen, setIsOpen] = useState(false);
//   const menuRef = useRef<HTMLDivElement>(null);
//   const [opensUp, setOpensUp] = useState(false);
//   useEffect(() => {
//     const handleClickOutside = (event: MouseEvent) => {
//       if (menuRef.current && !menuRef.current.contains(event.target as Node))
//         setIsOpen(false);
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   const handleToggle = () => {
//     if (!isOpen && menuRef.current) {
//       const rect = menuRef.current.getBoundingClientRect();
//       const spaceBelow = window.innerHeight - rect.bottom;
//       const dropdownHeight = 85; // Estimated height of your dropdown menu
//       setOpensUp(spaceBelow < dropdownHeight);
//     }
//     setIsOpen(!isOpen);
//   };

//   const menuPositionClass = opensUp ? "bottom-full mb-2" : "mt-2";

//   return (
//     <div className="relative" ref={menuRef}>
//       <button
//         onClick={handleToggle}
//         className="p-2 h-8 w-8 flex items-center justify-center rounded-full hover:bg-slate-100"
//       >
//         <MoreVertical className="w-5 h-5 text-slate-600" />
//       </button>
//       {isOpen && (
//         <div
//           className={`absolute right-0 w-36 bg-white rounded-md shadow-lg border border-gray-300 z-10 ${menuPositionClass}`}
//         >
//           <button
//             onClick={() => {
//               onEdit();
//               setIsOpen(false);
//             }}
//             className="w-full text-left flex items-center gap-3 px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
//           >
//             <Pen className="w-4 h-4" />
//             <span>Edit</span>
//           </button>
//           <button
//             onClick={() => {
//               onDelete();
//               setIsOpen(false);
//             }}
//             className="w-full text-left flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
//           >
//             <Trash2 className="w-4 h-4" />
//             <span>Delete</span>
//           </button>
//         </div>
//       )}
//     </div>
//   );
// };

// --- MAIN PAGE COMPONENT ---
const SizeMasterPage: React.FC = () => {
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [segments, setSegments] = useState<TransformedSegmentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [itemToEdit, setItemToEdit] = useState<TransformedSegmentData | null>(
    null
  );
  const [itemToDelete, setItemToDelete] =
    useState<TransformedSegmentData | null>(null);

  const totalPages = Math.ceil(totalCount / pageSize);

  const fetchSizeSegments = useCallback(
    async (pageToFetch: number) => {
      setLoading(true);
      setFetchError(null);
      const params = new URLSearchParams({
        page: String(pageToFetch),
        limit: String(pageSize),
      });
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (searchInput) params.append("search", searchInput);
      try {
        const { data } = await axiosApi.get(
          `/api/sizeSegment/get-size-segments?${params.toString()}`
        );
        const { segments: apiSegments, totalCount: newTotalCount } = data.data;
        setSegments(
          apiSegments.map((s: IApiSizeSegment) => ({
            id: s._id,
            name: s.segmentName,
            status: s.status,
            // length: s.length,
            // width: s.width,
            sets: s.numberOfSets,
            lastUpdated: formatDateTime(s.updatedAt || s.createdAt),
          }))
        );
        setTotalCount(newTotalCount);
      } catch (err: any) {
        setFetchError(
          err?.response?.data?.message || "Failed to load size segments"
        );
        setSegments([]);
        setTotalCount(0);
      } finally {
        setLoading(false);
      }
    },
    [pageSize, statusFilter, searchInput]
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchSizeSegments(1);
      if (currentPage !== 1) setCurrentPage(1);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchInput, statusFilter]);

  useEffect(() => {
    fetchSizeSegments(currentPage);
  }, [currentPage, fetchSizeSegments]);

  const handleSaveSegment = async (formData: NewSegmentData, id?: string) => {
    const isEditing = !!id;
    const toastId = toast.loading(
      isEditing ? "Updating segment..." : "Saving segment..."
    );
    try {
      const payload = {
        ...formData,
        // length: parseInt(formData.length, 10),
        // width: parseInt(formData.width, 10),
        numberOfSets: parseInt(formData.numberOfSets, 10),
      };
      const response = isEditing
        ? await axiosApi.put(
            `/api/sizeSegment/update-size-segment/${id}`,
            payload
          )
        : await axiosApi.post("/api/sizeSegment/create-size-segment", payload);
      toast.success(response.data.message, { id: toastId });
      setIsFormVisible(false);
      setItemToEdit(null);
      fetchSizeSegments(isEditing ? currentPage : 1);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "An error occurred", {
        id: toastId,
      });
    }
  };

  const confirmDelete = useCallback(async () => {
    if (!itemToDelete) return;
    const toastId = toast.loading("Deleting segment...");
    try {
      await axiosApi.delete(
        `/api/sizeSegment/delete-size-segment/${itemToDelete.id}`
      );
      toast.success("Segment deleted successfully!", { id: toastId });
      if (segments.length === 1 && currentPage > 1) {
        setCurrentPage(currentPage - 1);
      } else {
        fetchSizeSegments(currentPage);
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Failed to delete segment.", {
        id: toastId,
      });
    } finally {
      setItemToDelete(null);
    }
  }, [itemToDelete, currentPage, segments.length, fetchSizeSegments]);

  const handleEditClick = (segment: TransformedSegmentData) => {
    setItemToEdit(segment);
    setIsFormVisible(true);
  };

  const handleCancelForm = () => {
    setIsFormVisible(false);
    setItemToEdit(null);
  };

  return (
    <div className="bg-slate-50 min-h-screen font-sans">
      {isFormVisible ? (
        <CreateSizeSegmentForm
          onSave={handleSaveSegment}
          onCancel={handleCancelForm}
          initialData={
            itemToEdit
              ? {
                  id: itemToEdit.id,
                  segmentName: itemToEdit.name,
                  status: itemToEdit.status,
                  // length: String(itemToEdit.length),
                  // width: String(itemToEdit.width),
                  numberOfSets: String(itemToEdit.sets),
                }
              : null
          }
        />
      ) : (
        <div className="animate-fade-in">
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-6">
            <div className="flex items-center gap-10">
              <Link
                to="/master"
                className="flex items-center gap-2 border bg-white border-slate-200 px-3 py-2.5 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="text-black font-medium">
                  Back <span className="hidden sm:inline">to Masters</span>
                </span>
              </Link>
              <div>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-800">
                  Size Master
                </h1>
                <p className="text-slate-600 text-sm sm:text-base mt-1">
                  Manage size segments and their set configurations
                </p>
              </div>
            </div>

            <Button
              onClick={() => setIsFormVisible(true)}
              className="bg-green-500 hover:bg-green-600 text-white"
            >
              <Plus className="w-5 h-5 mr-2" /> New Size Segment
            </Button>
          </div>

          <div className="mt-6 bg-white shadow-md rounded-lg p-4">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1 w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search by segment name..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="pl-10"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full md:w-auto h-10 border border-slate-300 rounded-md px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
              >
                <option value="all">All Status</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="mt-6 bg-white shadow-lg rounded-lg">
            {loading ? (
              <div className="flex items-center justify-center gap-2 p-12 text-slate-500">
                <Loader2 className="w-6 h-6 animate-spin" />
                <span>Loading Segments...</span>
              </div>
            ) : fetchError ? (
              <div className="p-12 text-center text-red-600 font-medium">
                {fetchError}
              </div>
            ) : segments.length === 0 ? (
              <div className="p-12 text-center text-slate-500">
                No segments found. Create one to get started!
              </div>
            ) : (
              <>
                <div className="hidden lg:block">
                  <ShadcnTable>
                    <ShadcnTableHeader>
                      <ShadcnTableRow>
                        <ShadcnTableHead>Segment Name</ShadcnTableHead>
                        {/* <ShadcnTableHead>Dimensions (L x W)</ShadcnTableHead> */}
                        <ShadcnTableHead>pieces</ShadcnTableHead>
                        <ShadcnTableHead>Status</ShadcnTableHead>
                        <ShadcnTableHead>Last Updated</ShadcnTableHead>
                        <ShadcnTableHead className="text-center">
                          Actions
                        </ShadcnTableHead>
                      </ShadcnTableRow>
                    </ShadcnTableHeader>
                    <ShadcnTableBody>
                      {segments.map((segment) => (
                        <ShadcnTableRow key={segment.id}>
                          <ShadcnTableCell className="font-medium text-slate-900">
                            {segment.name}
                          </ShadcnTableCell>
                          {/* <ShadcnTableCell>
                            {segment.length} x {segment.width}
                          </ShadcnTableCell> */}
                          <ShadcnTableCell>{segment.sets}</ShadcnTableCell>
                          <ShadcnTableCell>
                            <Badge
                              variant={
                                segment.status === "Active"
                                  ? "success"
                                  : "danger"
                              }
                            >
                              {segment.status}
                            </Badge>
                          </ShadcnTableCell>
                          <ShadcnTableCell>
                            {new Date(segment.lastUpdated).toLocaleDateString(
                              "en-IN",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              }
                            )}
                          </ShadcnTableCell>
                          <ShadcnTableCell className="flex justify-center items-center">
                            <ActionMenu
                              onEdit={() => handleEditClick(segment)}
                              onDelete={() => setItemToDelete(segment)}
                            />
                          </ShadcnTableCell>
                        </ShadcnTableRow>
                      ))}
                    </ShadcnTableBody>
                  </ShadcnTable>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 lg:hidden">
                  {segments.map((segment) => (
                    <div
                      key={segment.id}
                      className="bg-white rounded-lg shadow-md border border-gray-300 p-4 flex flex-col justify-between"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="flex-shrink-0 bg-slate-100 p-3 rounded-full">
                            <Package className="text-gray-500 w-6 h-6" />
                          </div>
                          <div>
                            <h3 className="font-bold text-lg text-slate-900">
                              {segment.name}
                            </h3>
                            {/* <p className="text-sm text-slate-600">
                              Dims: {segment.length} x {segment.width}
                            </p> */}
                          </div>
                        </div>
                        <ActionMenu
                          onEdit={() => handleEditClick(segment)}
                          onDelete={() => setItemToDelete(segment)}
                        />
                      </div>
                      <div className="mt-4 space-y-2 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-slate-500">Status:</span>
                          <Badge
                            variant={
                              segment.status === "Active" ? "success" : "danger"
                            }
                          >
                            {segment.status}
                          </Badge>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Sets:</span>
                          <span className="font-medium text-slate-800">
                            {segment.sets}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-slate-500">Last Updated:</span>
                          <span className="font-medium text-slate-800">
                            {new Date(segment.lastUpdated).toLocaleDateString(
                              "en-IN",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              }
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {totalPages > 1 && (
                  <div className="flex items-center justify-between flex-wrap gap-4 p-4 border-t">
                    <span className="text-sm text-slate-600">
                      Showing{" "}
                      <span className="font-medium">{segments.length}</span> of{" "}
                      <span className="font-medium">{totalCount}</span> results
                    </span>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setCurrentPage((p) => p - 1)}
                        disabled={currentPage === 1 || loading}
                      >
                        Previous
                      </Button>
                      <span className="text-sm font-medium px-2">
                        Page {currentPage} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        onClick={() => setCurrentPage((p) => p + 1)}
                        disabled={currentPage >= totalPages || loading}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
      {itemToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md m-4 p-6 animate-fade-in-up">
            <h2 className="text-lg font-semibold text-slate-900">
              Confirm Deletion
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Are you sure you want to delete segment:{" "}
              <span className="font-bold">{itemToDelete.name}</span>? This
              action cannot be undone.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="outline" onClick={() => setItemToDelete(null)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={confirmDelete}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SizeMasterPage;
